import { cn } from '@apps/web/lib/utils';

/**
 * The one canonical vocabulary for status-pill color across the app.
 *
 * Every accepted/pending/declined/needs-review style pill resolves to one of
 * these five intents, and each intent has exactly one hue. Call sites declare
 * the *intent* they mean rather than hard-coding a Tailwind palette, so the
 * same status word can stay neutral on one surface and negative on another
 * (e.g. an "unfilled" outreach in a list vs. an unfilled shift in a table)
 * while every badge of a given intent looks identical.
 *
 * Pills use statusIntentBadgeClassName; dots and larger surfaces use the base
 * tokens directly. Call sites should not hard-code semantic color palettes.
 */
export type StatusIntent =
    | 'positive'
    | 'info'
    | 'warning'
    | 'negative'
    | 'neutral';

export type StatusIntentTokens = {
    /** Solid dot indicator (e.g. leading badge dot, response-count chips). */
    readonly dot: string;
    /** Larger-surface tint; pills use statusIntentBadgeClassName instead. */
    readonly background: string;
    /** Foreground/label color. */
    readonly text: string;
    /** Larger-surface outline; status pills use a transparent border. */
    readonly border: string;
};

export const STATUS_INTENT_TOKENS: Record<StatusIntent, StatusIntentTokens> = {
    positive: {
        dot: 'bg-emerald-500',
        background: 'bg-emerald-50 dark:bg-emerald-950/50',
        text: 'text-emerald-700 dark:text-emerald-300',
        border: 'border-emerald-200 dark:border-emerald-900',
    },
    info: {
        dot: 'bg-blue-500',
        background: 'bg-blue-50 dark:bg-blue-950/50',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-900',
    },
    warning: {
        dot: 'bg-amber-500',
        background: 'bg-amber-50 dark:bg-amber-950/50',
        text: 'text-amber-800 dark:text-amber-300',
        border: 'border-amber-200 dark:border-amber-900',
    },
    negative: {
        dot: 'bg-rose-500',
        background: 'bg-rose-50 dark:bg-rose-950/50',
        text: 'text-rose-700 dark:text-rose-300',
        border: 'border-rose-200 dark:border-rose-900',
    },
    neutral: {
        dot: 'bg-slate-400',
        background: 'bg-slate-50 dark:bg-muted',
        text: 'text-slate-600 dark:text-muted-foreground',
        border: 'border-slate-200 dark:border-border',
    },
};

/**
 * Status pills use a faint tint of their label color without a colored outline.
 * Keep the base tokens above for dots and larger surfaces such as alert panels.
 */
export function statusIntentBadgeClassName(intent: StatusIntent): string {
    const tokens = STATUS_INTENT_TOKENS[intent];
    return cn(
        'border-transparent bg-[color-mix(in_srgb,currentColor_4%,var(--card))] [a&]:hover:bg-[color-mix(in_srgb,currentColor_8%,var(--card))]',
        tokens.text,
    );
}
